<template>
  <!-- The API's plans: a monthly/annual switch over one featured tier and the
       enterprise tier. Shared by /api and /pricing; each page decides where a
       tier's button goes through the `cta` slot ({ tier, frequency }).

       Deliberately no stacking context of its own: the featured plan's z-index
       has to reach the page's, so it can hang over the next section. -->
  <div class="relative">
    <div class="relative z-10 text-center">
      <BaseHeading size="title" class="mx-auto max-w-4xl">One simple plan, full access</BaseHeading>
      <p class="mx-auto mt-4 max-w-2xl text-lg leading-8 text-gray-400">
        No tiers, one click cancellation. No questions asked. Enjoy full access to our data API with a single, affordable plan.
      </p>
      <div class="mt-10 flex justify-center">
        <SegmentedControl
          :options="FREQUENCIES"
          :model-value="frequency"
          label="Payment frequency"
          size="sm"
          @select="frequency = $event"
        />
      </div>
    </div>

    <div class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-36 lg:-mb-14 lg:max-w-none lg:grid-cols-2">
      <svg viewBox="0 0 1208 1024" aria-hidden="true" class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0">
        <ellipse cx="604" cy="512" :fill="`url(#${glowId})`" rx="604" ry="512" />
        <defs>
          <radialGradient :id="glowId">
            <stop :stop-color="GLOW[0]" />
            <stop offset="1" :stop-color="GLOW[1]" />
          </radialGradient>
        </defs>
      </svg>
      <div class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10" aria-hidden="true" />

      <div
        v-for="tier in tiers"
        :key="tier.id"
        class="relative rounded-2xl"
        :class="tier.featured ? 'z-10 bg-white shadow-xl ring-1 ring-gray-900/10' : 'bg-gray-800/80 ring-1 ring-white/10 lg:bg-transparent lg:pb-14 lg:ring-0'"
      >
        <div class="p-8 lg:pt-12 xl:p-10 xl:pt-14">
          <h3 :id="tier.id" class="text-sm font-semibold leading-6" :class="tier.featured ? 'text-gray-900' : 'text-white'">{{ tier.name }}</h3>
          <div class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch">
            <div class="mt-2 flex items-center gap-x-4">
              <p class="text-4xl font-bold tracking-tight" :class="tier.featured ? 'text-gray-900' : 'text-white'">{{ tier.price[frequency] }}</p>
              <div v-if="!tier.custom" class="text-sm leading-5">
                <p :class="tier.featured ? 'text-gray-900' : 'text-white'">USD</p>
                <p :class="tier.featured ? 'text-gray-500' : 'text-gray-400'">/month</p>
              </div>
            </div>
            <slot name="cta" :tier="tier" :frequency="frequency" />
          </div>
          <div class="mt-8 flow-root sm:mt-10">
            <ul
              role="list"
              class="-my-2 divide-y border-t text-sm leading-6 lg:border-t-0"
              :class="tier.featured ? 'divide-gray-900/5 border-gray-900/5 text-gray-600' : 'divide-white/5 border-white/5 text-white'"
            >
              <li v-for="feature in tier.mainFeatures" :key="feature.label || feature" class="flex gap-x-3 py-2">
                <CheckIcon class="h-6 w-5 flex-none" :class="tier.featured ? 'text-indigo-600' : 'text-gray-500'" aria-hidden="true" />
                <p v-if="feature.note" class="font-bold">{{ feature.label }} <span class="block font-normal">{{ feature.note }}</span></p>
                <p v-else>{{ feature }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, useId } from 'vue'
import { CheckIcon } from '@heroicons/vue/20/solid'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import SegmentedControl from '~/components/molecules/SegmentedControl.vue'

defineProps({
  // [{ id, name, featured, custom?, price: { monthly, annually }, mainFeatures }]
  // A feature is a string, or { label, note } for a line with small print.
  tiers: { type: Array, required: true },
})

const FREQUENCIES = [
  { value: 'annually', label: 'Annually' },
  { value: 'monthly', label: 'Monthly' },
]

const frequency = ref('annually')

// The glow behind the featured tier: a pale lavender into indigo-500.
const GLOW = ['#bcbcfd', '#6366f1']
const glowId = `plans-glow-${useId()}`
</script>
